import React, { useContext, useEffect, useMemo, useState } from "react";
import MyContext from "../../MyContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ClientTicketDrawer from "./ClientTicketDrawer";
import RaiseTicketModal from "./RaiseTicketModal";
import ClientTicketCard from "./ClientTicketCard";
import { PlusCircle, RefreshCw, Search } from "lucide-react";
import "./client-ticket.css"; // NOTE: new file name with 'client-tickets-' prefix

const ClientSupportTickets = ({ userID }) => {
  const { baseUrl, config } = useContext(MyContext);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  // single universal search
  const [q, setQ] = useState("");

  // drawer
  const [openDrawer, setOpenDrawer] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);

  // modal
  const [openModal, setOpenModal] = useState(false);

  const fetchTickets = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/api/Support/v1.0/getSupportTicket`, {
        method: "POST",
        headers: { ...config.headers, "Content-Type": "application/json" },
        body: JSON.stringify({ userid: userID || "" }),
      });
      const data = await res.json();
      if (data?.responseCode === "00") {
        setTickets(data?.response || []);
      } else {
        setTickets([]);
        toast.error(data?.responseMessage || "Failed to fetch tickets");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error fetching tickets");
      setTickets([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [userID]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return tickets;
    return tickets.filter((t) => {
      return (
        (t.ticket_code || "").toLowerCase().includes(s) ||
        (t.subject || "").toLowerCase().includes(s) ||
        (t.description || "").toLowerCase().includes(s) ||
        (t.department || "").toLowerCase().includes(s) ||
        (t.priority || "").toLowerCase().includes(s) ||
        (t.status || "").toLowerCase().includes(s)
      );
    });
  }, [tickets, q]);

  const openDrawerFor = (t) => {
    setActiveTicket(t);
    setOpenDrawer(true);
  };

  return (
    <div className="client-tickets-wrap">
      <ToastContainer />

      {/* Header */}
      <div className="client-tickets-header border rounded admin-left-card p-3 text-white">
        <div>
          <h2 className="m-0">Support & Help</h2>
          <p className="m-0 client-tickets-subtext text-white">
            Track your issues, chat with support, and raise new tickets. Click a card to view details.
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-secondary d-inline-flex align-items-center gap-2"
            onClick={() => fetchTickets()}
            disabled={loading}
          >
            <RefreshCw size={16} />
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button
            className="btn btn-primary d-inline-flex align-items-center gap-2"
            onClick={() => setOpenModal(true)}
          >
            <PlusCircle size={18} />
            Raise Ticket
          </button>
        </div>
      </div>

      {/* Universal Search */}
      <div className="client-tickets-search">
        <Search size={18} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by code, subject, description, department, status…"
        />
      </div>

      {/* Cards Grid */}
      <div className="client-tickets-grid">
        {loading ? (
          <div className="client-tickets-loader">
            <div className="spinner-border" role="status" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="client-tickets-empty">
            <div className="client-tickets-empty-card">
              <h5>No tickets found</h5>
              <p className="mb-3">
                Try a different search. You can also create a new ticket for any issue you’re facing.
              </p>
              <button className="btn btn-primary" onClick={() => setOpenModal(true)}>
                <PlusCircle size={18} className="me-1" />
                Raise New Ticket
              </button>
            </div>
          </div>
        ) : (
          <>
            {filtered.map((t) => (
              <ClientTicketCard key={t.pk_ticket_id} ticket={t} onOpen={() => openDrawerFor(t)} />
            ))}

            {/* “Raise New Ticket” card at the end, same size */}
            <div className="client-tickets-card client-tickets-card--raise" onClick={() => setOpenModal(true)}>
              <div className="client-tickets-card-raise-content">
                <PlusCircle size={28} />
                <div className="client-tickets-raise-title">Raise New Ticket</div>
                <div className="client-tickets-raise-sub">
                  Describe your issue and we’ll get back to you quickly.
                </div>
                <div className="client-tickets-raise-cta">Create Ticket</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Drawer + Modal */}
      <ClientTicketDrawer
        open={openDrawer}
        onClose={(refetch) => {
          setOpenDrawer(false);
          setActiveTicket(null);
          if (refetch) fetchTickets(true);
        }}
        ticket={activeTicket}
        userID={userID}
      />

      <RaiseTicketModal
        open={openModal}
        onClose={(didCreate) => {
          setOpenModal(false);
          if (didCreate) fetchTickets(true);
        }}
        userID={userID}
      />
    </div>
  );
};

export default ClientSupportTickets;
